import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Download, Truck } from "lucide-react";

const STATUS_STYLES = {
  paid: "text-emerald-700 border-emerald-200 bg-emerald-50",
  completed: "text-emerald-700 border-emerald-200 bg-emerald-50",
  captured: "text-emerald-700 border-emerald-200 bg-emerald-50",
  pending: "text-yellow-800 border-yellow-200 bg-yellow-50",
  failed: "text-red-700 border-red-200 bg-red-50",
};

// Iter48 — delivery workflow status pill colors.
const DELIVERY_STYLES = {
  pending: "text-yellow-800 border-yellow-200 bg-yellow-50",
  sent: "text-blue-700 border-blue-200 bg-blue-50",
  completed: "text-emerald-700 border-emerald-200 bg-emerald-50",
  n_a: "text-muted-foreground border-border bg-background",
};

const AdminOrders = () => {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [status, setStatus] = useState("all");
  const [gateway, setGateway] = useState("all");
  const [delivery, setDelivery] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (status !== "all") params.set("status", status);
      if (gateway !== "all") params.set("gateway", gateway);
      if (delivery !== "all") params.set("delivery", delivery);
      params.set("skip", String(skip));
      params.set("limit", String(limit));
      const { data } = await api.get(`/admin/orders?${params}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load orders", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, [debouncedQ, status, gateway, delivery, skip, limit]);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "orders.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error("Export failed", { description: e.response?.data?.detail || "" }); }
  };

  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(skip / limit) + 1;

  return (
    <AdminLayout title="Orders" actions={
      <Button data-testid="export-orders-csv" onClick={downloadCsv} variant="outline" className="rounded-none">
        <Download size={14} className="mr-2" /> Export CSV
      </Button>
    }>
      <div className="border border-border bg-card p-4 mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="order-search" placeholder="Search by session id, email, purchase id…" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} className="rounded-none h-11 pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
          <SelectTrigger data-testid="order-status-filter" className="rounded-none h-11 w-full md:w-48"><SelectValue placeholder="Payment status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={gateway} onValueChange={(v) => { setGateway(v); setSkip(0); }}>
          <SelectTrigger data-testid="order-gateway-filter" className="rounded-none h-11 w-full md:w-48"><SelectValue placeholder="Gateway" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All gateways</SelectItem>
            <SelectItem value="stripe">Stripe</SelectItem>
            <SelectItem value="paypal">PayPal</SelectItem>
            <SelectItem value="razorpay">Razorpay</SelectItem>
          </SelectContent>
        </Select>
        <Select value={delivery} onValueChange={(v) => { setDelivery(v); setSkip(0); }}>
          <SelectTrigger data-testid="order-delivery-filter" className="rounded-none h-11 w-full md:w-48"><SelectValue placeholder="Delivery" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All deliveries</SelectItem>
            <SelectItem value="required">Manual delivery required</SelectItem>
            <SelectItem value="pending">Delivery pending</SelectItem>
            <SelectItem value="sent">Delivery sent</SelectItem>
            <SelectItem value="completed">Delivery completed</SelectItem>
            <SelectItem value="not_required">Auto delivery only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card">
        <div className="hidden md:grid grid-cols-[180px_1fr_140px_100px_120px_120px_140px_100px] gap-3 items-center px-4 py-3 border-b border-border bg-background text-[11px] uppercase tracking-widest text-muted-foreground">
          <div>Order</div><div>Customer</div><div>Date</div><div>Gateway</div><div>Amount</div><div>Status</div><div>Delivery</div><div></div>
        </div>
        {loading && <div className="p-8 text-center text-muted-foreground text-sm" data-testid="orders-loading">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-12 text-center text-sm text-muted-foreground" data-testid="orders-empty">No orders match.</div>}
        {!loading && items.map((o) => {
          const deliveryReq = !!o.manual_delivery_required;
          const dStat = o.delivery_status || (deliveryReq ? "pending" : "n_a");
          return (
          <div key={o.session_id} data-testid={`order-row-${o.session_id}`} className="grid grid-cols-1 md:grid-cols-[180px_1fr_140px_100px_120px_120px_140px_100px] gap-3 items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-background/60">
            <div className="text-xs font-mono text-muted-foreground truncate">{o.session_id.slice(-16)}</div>
            <div className="text-sm truncate">{o.user_email || <span className="text-muted-foreground">Guest</span>}</div>
            <div className="text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</div>
            <div className="text-xs uppercase">{o.gateway || o.provider || "stripe"}</div>
            <div className="text-sm">{(o.currency || "").toUpperCase()} {o.amount}</div>
            <div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[o.payment_status] || "text-muted-foreground border-border"}`}>
                {o.payment_status || "—"}
              </span>
            </div>
            <div>
              {deliveryReq ? (
                <span data-testid={`delivery-badge-${o.session_id}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border ${DELIVERY_STYLES[dStat] || DELIVERY_STYLES.pending}`}>
                  <Truck size={10} /> {dStat === "n_a" ? "pending" : dStat}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">auto</span>
              )}
            </div>
            <div className="justify-self-end">
              <Link to={`/admin/orders/${o.session_id}`} data-testid={`view-order-${o.session_id}`} className="text-xs text-accent hover:underline">View →</Link>
            </div>
          </div>
          );
        })}
      </div>
      {total > limit && (
        <div className="flex justify-between items-center mt-4 text-sm" data-testid="orders-pagination">
          <div className="text-muted-foreground">Page {page} of {pages} · {total} total</div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Previous</Button>
            <Button variant="outline" className="rounded-none" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Next</Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
